import { IconGoogleBusiness, IconGoogleSearch } from '../icons';
// =============================================================
// GoogleConnectionCard  Phase 4
// Organization-level Google Account Connection Card
// =============================================================

import React, { useState } from 'react';
import { GoogleConnectionDto } from '../../lib/api';
import { ConnectionStatus } from './ConnectionStatus';

interface Props {
  configured: boolean;
  connected: boolean;
  connection: GoogleConnectionDto | null;
  onConnect: () => Promise<void>;
  onDisconnect: () => Promise<void>;
  loading: boolean;
}

export const GoogleConnectionCard: React.FC<Props> = ({
  configured,
  connected,
  connection,
  onConnect,
  onDisconnect,
  loading,
}) => {
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      await onConnect();
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      await onDisconnect();
      setConfirmDisconnect(false);
    } finally {
      setDisconnecting(false);
    }
  };

  return (
    <div className="card integration-dashboard-card" style={{ padding: '24px', background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <div
            style={{
              width: '48px',
              height: '48px',
              borderRadius: '12px',
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.6rem',
            }}
          >
            <IconGoogleBusiness style={{ color: '#4285F4', width: 24, height: 24 }} />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#0f172a' }}>
              Google Account Connection
            </h3>
            <span style={{ fontSize: '0.82rem', color: '#64748b' }}>
              Multi-tenant OAuth 2.0 connection for your organization
            </span>
          </div>
        </div>

        <div>
          {!configured ? (
            <span className="badge badge-amber" style={{ padding: '4px 10px', borderRadius: '9999px', fontSize: '0.8rem', fontWeight: 600 }}>
              Google OAuth Not Configured
            </span>
          ) : (
            <ConnectionStatus
              status={
                connected && connection?.status === 'ACTIVE'
                  ? 'ACTIVE'
                  : connection?.status === 'EXPIRED'
                  ? 'EXPIRED'
                  : connection?.status === 'REVOKED'
                  ? 'REVOKED'
                  : 'NOT_CONNECTED'
              }
              label={
                connected && connection?.status === 'ACTIVE'
                  ? 'Connected'
                  : connection?.status === 'EXPIRED'
                  ? 'Expired  Reconnect'
                  : 'Not Connected'
              }
            />
          )}
        </div>
      </div>

      <p style={{ margin: '0 0 16px', fontSize: '0.9rem', color: '#475569', lineHeight: 1.5 }}>
        Connect your organization's Google account securely using OAuth 2.0. This allows read-only access to Google Search Console performance data and Google Business Profile metrics without ever sharing your password.
      </p>

      {!configured && (
        <div style={{ marginBottom: '16px', padding: '12px', background: '#fffbeb', border: '1px solid #fef3c7', borderRadius: '8px', fontSize: '0.85rem', color: '#92400e' }}>
          <strong>Configuration Required:</strong> Google OAuth credentials (<code style={{ background: '#fef3c7', padding: '1px 4px', borderRadius: '3px' }}>GOOGLE_CLIENT_ID</code> and <code style={{ background: '#fef3c7', padding: '1px 4px', borderRadius: '3px' }}>GOOGLE_CLIENT_SECRET</code>) have not been set in the server environment. Please refer to <code style={{ background: '#fef3c7', padding: '1px 4px', borderRadius: '3px' }}>docs/GOOGLE-INTEGRATION.md</code> to configure your Google Cloud project.
        </div>
      )}

      {connected && connection && (
        <div style={{ marginBottom: '16px', padding: '14px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '0.85rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
            <div>
              <span style={{ color: '#64748b', fontSize: '0.78rem', display: 'block' }}>Connected Account</span>
              <strong style={{ color: '#0f172a' }}>{connection.googleAccountEmail || 'Google Account'}</strong>
            </div>
            <div>
              <span style={{ color: '#64748b', fontSize: '0.78rem', display: 'block' }}>Authorized Scopes</span>
              <span style={{ color: '#334155', fontSize: '0.8rem' }}>
                Search Console (Read-Only) & Business Profile (Read-Only)
              </span>
            </div>
            <div>
              <span style={{ color: '#64748b', fontSize: '0.78rem', display: 'block' }}>Connected Since</span>
              <span style={{ color: '#334155' }}>{new Date(connection.createdAt).toLocaleDateString()}</span>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
        {!connected ? (
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleConnect}
            disabled={!configured || connecting || loading}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
          >
            <span></span>
            <span>{connecting ? 'Redirecting to Google...' : 'Connect Google Account'}</span>
          </button>
        ) : (
          <>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={handleConnect}
              disabled={connecting || loading}
              title="Reconnect to grant updated permissions or refresh tokens"
            >
              <span></span> Reconnect Google
            </button>

            {!confirmDisconnect ? (
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={() => setConfirmDisconnect(true)}
                disabled={disconnecting}
              >
                Disconnect
              </button>
            ) : (
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '0.82rem', color: '#b91c1c' }}>Disconnect Google?</span>
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={handleDisconnect}
                  disabled={disconnecting}
                >
                  {disconnecting ? 'Disconnecting...' : 'Yes, Disconnect'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setConfirmDisconnect(false)}
                  disabled={disconnecting}
                >
                  Cancel
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
